import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NavbarAr from "@/components/NavbarAr";
import FooterAr from "@/components/FooterAr";
import EventName from "@/components/EventName";
import Brand from "@/components/Brand";
import { IMAGES } from "@/lib/brand";

// ABOUT page — approved institutional narrative (July 2026).
// Purpose: explains WHY CrewZNatioN exists, in complement to the Homepage
// (which explains WHAT CrewZNatioN is). Four narrative movements without
// overlines: Beginning → Continuation → Purpose → The Gathering, closed by
// a single purpose sentence that names CrewZNatioN.
//
// Bilingual (Feb 2026): the `locale` prop selects the Arabic nav/footer,
// toggles `dir="rtl"`, and swaps every editorial string to its
// owner-approved Arabic equivalent. Structure, imagery, colours,
// typography hierarchy, and spacing are byte-identical between /about
// and /ar/about — only language + direction differ.

export default function About({ locale = "en" }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const PageFooter = isAr ? FooterAr : Footer;
    return (
        <div dir={isAr ? "rtl" : "ltr"} className="min-h-screen bg-[var(--crewz-bg)]" data-testid="about-page">
            <Nav />

            {/* HERO */}
            <section className="relative h-[60vh] md:h-[70vh] flex items-end overflow-hidden border-b border-white/5">
                <div className="absolute inset-0">
                    <img
                        src={IMAGES.riyadh}
                        alt={isAr ? "أفق الرياض" : "Riyadh skyline"}
                        className="w-full h-full object-cover opacity-80 kenburns"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--crewz-bg)] via-[rgba(17,17,17,0.2)] to-transparent" />
                </div>
                <div className="relative px-6 md:px-12 lg:px-20 pb-16 md:pb-20 max-w-6xl">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="gold-rule" />
                        <p
                            className="overline overline-primary"
                            style={{
                                textShadow:
                                    "0 1px 2px rgba(0,0,0,0.55), 0 0 10px rgba(0,0,0,0.5), 0 0 22px rgba(0,0,0,0.35)",
                            }}
                        >
                            {isAr ? <>عن <Brand /></> : <>About <Brand /></>}
                        </p>
                    </div>
                    <h1
                        className={`font-display text-5xl md:text-7xl lg:text-[5.5rem] text-[var(--crewz-text)] leading-[0.98] tracking-[-0.015em]${isAr ? " !leading-[62px] md:!leading-[88.4px] lg:!leading-[107.6px]" : ""}`}
                        data-testid="about-title"
                    >
                        {isAr ? (
                            <>قبل أن تصبح <Brand /> مؤسسة، بدأت بقناعة راسخة.</>
                        ) : (
                            <>Before it was an institution, <Brand /> was a conviction.</>
                        )}
                    </h1>
                </div>
            </section>

            {/* BODY — four narrative movements + closing sentence. */}
            <section
                className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-4xl mx-auto"
                data-testid="about-body"
            >
                {/* Movement A — Beginning */}
                <p
                    className="font-body text-lg md:text-xl text-[var(--crewz-text)]/90 leading-[1.85] font-light"
                    data-testid="about-movement-a"
                >
                    {isAr ? (
                        <>انطلقت <Brand /> في مدينة الرياض عام 2016 انطلاقًا من قناعةٍ بسيطة: أن ثقافة السيارات والدراجات النارية تستحق أن تُصان بنفس القدر من العناية التي تستحقها المركبات نفسها. ومع ترسيخ هذه القناعة، كان لا بد من بناء مجتمعٍ يحافظ على هذه المعايير ويصونها عبر الزمن، حتى أصبحت <Brand /> مؤسسةً تتولى مسؤولية الحفاظ عليها واستمرارها.</>
                    ) : (
                        <><Brand /> was founded in Riyadh in 2016 on one simple belief: that automotive and motorcycle culture is worth preserving with the same care the machines themselves deserve. Living up to that belief required a community capable of holding standards over time — and eventually, an institution capable of stewarding them.</>
                    )}
                </p>

                {/* Movement B — Continuation */}
                <p
                    className="font-body text-lg md:text-xl text-[var(--crewz-text)]/90 leading-[1.85] font-light mt-10"
                    data-testid="about-movement-b"
                >
                    {isAr ? (
                        <>وخلال الأعوام التي تلت ذلك، واصلت <Brand /> نموها بثبات. انضم الأعضاء بعضهم إلى بعض، وأصبحت اللقاءات أكثر تنظيمًا، وامتدت الرحلات إلى ما هو أبعد من حدود المدينة، بينما ترسخت المعايير عامًا بعد عام. وهكذا، تطور ما بدأ كمجتمعٍ يجمع الشغوفين إلى مؤسسةٍ تواصل حمل هذه المسؤولية، وتُدار اليوم باحترافية، وفاءً للقيم والأسس التي قامت عليها منذ البداية.</>
                    ) : (
                        <>Over the decade since, <Brand /> has grown continuously. Members brought other members. Meets became more considered. Journeys extended beyond the city. Standards deepened. What began as a community of enthusiasts developed the responsibilities of an institution, and <Brand /> today is professionally governed on that foundation.</>
                    )}
                </p>

                {/* Movement C — Purpose */}
                <p
                    className="font-body text-lg md:text-xl text-[var(--crewz-text)]/90 leading-[1.85] font-light mt-10"
                    data-testid="about-movement-c"
                >
                    {isAr ? (
                        <>تقوم <Brand /> على رسالةٍ واضحة: الحفاظ على ثقافة السيارات والدراجات النارية، وتطويرها، وتعزيز الروابط بين أفرادها. فالحفاظ يعني التمسك بمعايير الجودة، والسلوك، وروح المجتمع. والتطوير يعني تهيئة الطريق أمام الجيل القادم من الشغوفين، والمطورين، وقائدي الدراجات، ليواصلوا مسيرة ثقافةٍ تستحق أن تُورث. أما الترابط، فيعني الإيمان بأن قوة هذه الثقافة تنبع من استمرار الروابط بين الأفراد، والمدن، والأجيال، لأنها تزدهر حين يبقى المجتمع متصلًا عبر الزمن.</>
                    ) : (
                        <><Brand /> exists to preserve, develop, and connect the culture surrounding exceptional machines and the people who care for them. Preservation means holding to standards of craft, of conduct, and of company. Development means making room for the next generation of enthusiasts, builders, and riders to inherit a culture worth inheriting. Connection means recognising that automotive and motorcycle culture is at its strongest when people, cities, and generations remain in company with one another.</>
                    )}
                </p>

                {/* Movement D — The Gathering in relation to CrewZNatioN */}
                <p
                    className="font-body text-lg md:text-xl text-[var(--crewz-text)]/90 leading-[1.85] font-light mt-10"
                    data-testid="about-movement-d"
                >
                    {isAr ? (
                        <>يمثل <EventName /> أوضح تجسيدٍ لما يبنيه هذا المجتمع عامًا بعد عام. ففيه يرى الجمهور، في مكانٍ واحد، ثمرة سنواتٍ من العمل، والتنظيم، وبناء المجتمع. لكنه ليس بداية <Brand />، وليس كل ما تمثله، بل هو الامتداد الطبيعي لما بناه المجتمع معًا، وأبرز ثمرةٍ لمسيرةٍ استمرت عبر السنوات.</>
                    ) : (
                        <><EventName /> is the most visible expression of this community&rsquo;s work — the occasion on which the wider public sees, in one place, what years of building have produced. It is not the origin of <Brand />. It is not the whole of <Brand />. It is the flagship outcome of what the community has built together.</>
                    )}
                </p>

                {/* Closing sentence — brings the narrative back to CrewZNatioN */}
                <p
                    className={`font-display text-2xl md:text-3xl text-[var(--crewz-text)] leading-[1.35] mt-16 md:mt-20 tracking-[-0.005em]${isAr ? " !leading-[38.4px] md:!leading-[46.5px]" : ""}`}
                    data-testid="about-closing"
                >
                    {isAr ? (
                        <>توجد <Brand /> لتكون أمينةً على هذه الثقافة، ولتبقيها دائمًا بين أفراد المجتمع الذين يؤمنون بها ويعتنون بها، لتبقى حيّةً ومتوارثةً بين الأجيال.</>
                    ) : (
                        <><Brand /> exists to hold this culture in trust — and to keep it in company with the people who care for it.</>
                    )}
                </p>
            </section>

            {/* ORIGIN / PHILOSOPHY — WHY → HOW IT STARTED → WHAT IT
                BECAME → WHERE IT'S GOING. Anchored on the locked brand
                statement:
                  "The machines brought us together. The people made us
                   a community. What we built together became a culture."
                Distilled from the founder's story; not autobiography. */}
            <section
                className="border-t border-white/10 px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-4xl mx-auto"
                data-testid={isAr ? "about-origin-ar" : "about-origin"}
            >
                <div className={`flex items-center gap-3 mb-6 ${isAr ? "justify-end" : ""}`}>
                    <span className="gold-rule" />
                    <p className={`overline overline-primary ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}>
                        {isAr ? "الأصل والفلسفة" : "Origin & Philosophy"}
                    </p>
                </div>

                <blockquote
                    className={`${isAr ? "font-arabic text-2xl md:text-3xl lg:text-4xl !leading-[1.45] text-right" : "font-display text-2xl md:text-3xl lg:text-4xl leading-[1.15]"} text-[var(--crewz-text)] max-w-3xl`}
                    data-testid={isAr ? "about-origin-statement-ar" : "about-origin-statement"}
                >
                    {isAr ? (
                        <>الآلات جمعتنا. والناس صنعوا منّا مجتمعًا. وما بنيناه معًا أصبح ثقافة.</>
                    ) : (
                        <>The machines brought us together. The people made us a community. What we built together became a culture.</>
                    )}
                </blockquote>

                <div className={`mt-14 max-w-3xl ${isAr ? "text-right" : ""}`}>
                    <p className={`overline mb-3 text-zinc-500 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}>
                        {isAr ? "لماذا" : "Why"}
                    </p>
                    <p
                        className={`${isAr ? "font-arabic text-base md:text-lg leading-[1.95]" : "font-body text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85`}
                        data-testid={isAr ? "about-origin-why-ar" : "about-origin-why"}
                    >
                        {isAr ? (
                            <>شغف السيارات والدراجات النارية ليس حالةً عابرة، بل انتماءٌ يمتد سنواتٍ ويعبر الحدود. وحين يجتمع الناس حول هذا الشغف، تظهر لغةٌ مشتركة لا تصنعها العلامة ولا السعر ولا الطراز، بل الآلة نفسها وما تُشعله في أصحابها.</>
                        ) : (
                            <>Automotive and motorcycle enthusiasm is not a phase; it is a lifelong sense of belonging that crosses borders. When people gather around that shared passion, a common language emerges — one that isn&rsquo;t written by badge, price or platform, but by the machine itself and what it lights up in the people who care about it.</>
                        )}
                    </p>
                </div>

                <div className={`mt-12 max-w-3xl ${isAr ? "text-right" : ""}`}>
                    <p className={`overline mb-3 text-zinc-500 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}>
                        {isAr ? "كيف بدأت" : "How it started"}
                    </p>
                    <p
                        className={`${isAr ? "font-arabic text-base md:text-lg leading-[1.95]" : "font-body text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85`}
                        data-testid={isAr ? "about-origin-start-ar" : "about-origin-start"}
                    >
                        {isAr ? (
                            <>عاش مؤسس <Brand /> سنواتٍ من التجربة الأمريكية في مشهدها الحيّ. وفي فلوريدا رأى مجتمعًا يعمل بمنطقٍ بسيط: إذا احتاج شغوفٌ إلى مساعدةٍ في مشروعٍ أو تركيبٍ، جاء غيره من الشغوفين، بأدواتهم وبسياراتٍ مختلفةٍ تمامًا، لمجرد أن أحدهم يحتاج يدًا. لم يكن الانتماء حينها مرهونًا بالعلامة ولا بالسعر ولا بالطراز، بل بالحبّ المشترك للآلة.</>
                        ) : (
                            <>Years in the United States gave the founder of <Brand /> a firsthand education in a living enthusiast scene. In Florida the community operated by a simple rule: if one enthusiast needed help with a project or a swap, others showed up — with their own tools, in very different cars — because another enthusiast needed a hand. Belonging was not decided by badge, price, or platform; the machine brought people together.</>
                        )}
                    </p>
                </div>

                <div className={`mt-12 max-w-3xl ${isAr ? "text-right" : ""}`}>
                    <p className={`overline mb-3 text-zinc-500 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}>
                        {isAr ? "ما الذي أصبحت عليه" : "What it became"}
                    </p>
                    <p
                        className={`${isAr ? "font-arabic text-base md:text-lg leading-[1.95]" : "font-body text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85`}
                        data-testid={isAr ? "about-origin-became-ar" : "about-origin-became"}
                    >
                        {isAr ? (
                            <>عند العودة إلى المملكة العربية السعودية، كان المشهد شغوفًا لكنه مبعثر بين مجموعاتٍ صغيرة تفصلها العلامة والطراز والأسلوب. فوُلدت <Brand /> لتوحّد الشغوفين على اختلاف مركباتهم حول ما يجمعهم: الحبّ المشترك للآلة، وسلوك الطريق، والاحترام، والاستمتاع بما نقود. فلسفتنا واضحة: إن كانت سيارتك أو دراجتك تُبهجك، فتلك الفرحة أثمن من السعر والوضع الاجتماعي — وتبقى <span className="text-[var(--crewz-gold)] font-medium">Smiles Per Liter</span> اللغة التي نُسمّي بها هذه الفرحة.</>
                        ) : (
                            <>Back in Saudi Arabia, the scene was passionate but fragmented across marque, platform and style — many small tribes, each closed around its own machine. <Brand /> was created to bring enthusiasts back together across those lines around what actually unites them: the machine, the road, respect, and the enjoyment of driving. The philosophy is simple: if your car or your motorcycle makes you smile, that shared passion matters more than price or status — and <span className="text-[var(--crewz-gold)] font-medium">Smiles Per Liter</span> remains the language we use for it.</>
                        )}
                    </p>
                </div>

                <div className={`mt-12 max-w-3xl ${isAr ? "text-right" : ""}`}>
                    <p className={`overline mb-3 text-zinc-500 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}>
                        {isAr ? "إلى أين المسير" : "Where it's going"}
                    </p>
                    <p
                        className={`${isAr ? "font-arabic text-base md:text-lg leading-[1.95]" : "font-body text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85`}
                        data-testid={isAr ? "about-origin-going-ar" : "about-origin-going"}
                    >
                        {isAr ? (
                            <>تعمل <Brand /> على تحويل هذه القناعة إلى مؤسسةٍ تُدار باحترافية، وثقافةٍ تستحق أن تُورث. طريقٌ مفتوح للجيل القادم من الشغوفين والمطورين وقائدي الدراجات، ليبقى المجتمع متصلًا عبر الأجيال والمدن، وتظل الآلات — كما كانت دائمًا — هي التي تجمعنا.</>
                        ) : (
                            <><Brand /> is turning that conviction into a professionally governed institution and a culture worth inheriting — a road held open for the next generation of enthusiasts, builders and riders, so that the community remains in company with itself across cities and across generations, and the machines — as they always have — continue to bring us together.</>
                        )}
                    </p>
                </div>
            </section>

            <PageFooter />
        </div>
    );
}
